import * as React from 'react';
import { Bot, RefreshCw, ArrowLeft } from 'lucide-react';
import apiService from '../../services/api';
import {
  executeSkillInsertions,
  getDocumentParagraphsForSkill,
} from '../../features/agent/skills/SkillWordExecutor';
import {
  localizeSkillMeta,
  SKILL_AGENT_LANGUAGE,
} from '../../features/agent/skills/builtInSkills';
import { getSkillRunnerCopy } from '../../features/agent/skills/skillRunnerCopy';
import { getStoredUserDisplayName } from '../../features/agent/skills/skillAuthor';
import type {
  AgentSkillMeta,
  SkillAnalyzeResult,
  SkillChatTurn,
  SkillExecuteResult,
  SkillOrderMode,
  SkillOrderingContext,
  SkillSessionState,
} from '../../features/agent/skills/types';
import { ChatComposer } from './ChatComposer';
import './SkillRunner.css';

function shouldOfferOuterClauseMatch(ctx: SkillOrderingContext | undefined): boolean {
  if (!ctx?.clarification_message?.trim()) return false;
  if (ctx.needs_ordering_clarification) return true;
  return !!(ctx.pasted_generic_numbering && ctx.document_has_outer_clause_refs);
}

let _turnId = 0;
function nextTurnId(): string {
  return `sk-turn-${++_turnId}-${Date.now()}`;
}

function makeInitialState(skill: AgentSkillMeta): SkillSessionState {
  const copy = getSkillRunnerCopy(SKILL_AGENT_LANGUAGE);
  const welcome: SkillChatTurn = {
    id: nextTurnId(),
    role: 'assistant',
    content: copy.welcomePaste,
  };
  return {
    skill,
    step: 'awaiting_paste',
    turns: [welcome],
    pastedText: '',
    analyzeResult: null,
    selectedLocationIndex: null,
    selectedLocationLabel: null,
    chronological: null,
    orderMode: 'pasted',
    formatMode: null,
    author: copy.defaultAuthor,
    executeResult: null,
    error: null,
  };
}

interface SkillRunnerProps {
  skill: AgentSkillMeta;
  onBack: () => void;
}

export const SkillRunner: React.FC<SkillRunnerProps> = ({ skill, onBack }) => {
  const displaySkill = React.useMemo(() => localizeSkillMeta(skill), [skill]);
  const copy = React.useMemo(() => getSkillRunnerCopy(SKILL_AGENT_LANGUAGE), []);

  const [session, setSession] = React.useState<SkillSessionState>(() => makeInitialState(skill));
  const [input, setInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [profileAuthorName, setProfileAuthorName] = React.useState(() => getStoredUserDisplayName());
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const executingRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getCurrentUser();
        if (!cancelled && res.success && res.data) {
          const name = (res.data.full_name || res.data.name || '').trim();
          if (name) {
            setProfileAuthorName(name);
            return;
          }
        }
      } catch {
        /* use stored user */
      }
      if (!cancelled) setProfileAuthorName(getStoredUserDisplayName());
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    setSession(makeInitialState(skill));
    setInput('');
  }, [skill.id]);

  React.useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [session.turns, session.step]);

  const appendTurn = (role: 'user' | 'assistant', content: string) => {
    setSession((s) => ({
      ...s,
      turns: [...s.turns, { id: nextTurnId(), role, content }],
    }));
  };

  const handlePasteSubmit = async (text: string) => {
    const pasted = text.trim();
    if (!pasted) {
      setSession((s) => ({ ...s, error: copy.errorEmptyPaste }));
      return;
    }
    setBusy(true);
    setSession((s) => ({ ...s, error: null, pastedText: pasted }));
    appendTurn('user', pasted);

    try {
      const paras = await getDocumentParagraphsForSkill();

      const res = await apiService.analyzeAgentSkill({
        skillId: skill.id,
        pastedText: pasted,
        paragraphs: paras,
        language: SKILL_AGENT_LANGUAGE,
      });

      if (!res.success || !res.data) {
        throw new Error(res.message || 'Analysis failed');
      }

      const analyzeResult: SkillAnalyzeResult = {
        client_requests: (res.data.client_requests || []).map((cr) => ({
          text: cr.text,
          clause_number: cr.clause_number || '',
        })),
        location_options: res.data.location_options || [],
        has_clause_numbers: !!res.data.has_clause_numbers,
        ordering_context: res.data.ordering_context,
      };

      const count = analyzeResult.client_requests.length;
      appendTurn('assistant', copy.foundRequests(count));

      setSession((s) => ({
        ...s,
        step: 'awaiting_location',
        analyzeResult,
        error: null,
      }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Analysis failed';
      setSession((s) => ({ ...s, error: msg }));
      appendTurn('assistant', copy.errorAnalyze(msg));
    } finally {
      setBusy(false);
    }
  };

  const handleAutoLocationSelect = () => {
    appendTurn('user', copy.decideYourself);
    const opts = session.analyzeResult?.location_options ?? [];
    const generalOpt = opts.find((o) => /כללי|הערות\s+כלליות|general/i.test(o.label));
    const fallbackIndex = generalOpt?.paragraph_index ?? opts[0]?.paragraph_index ?? 0;
    const fallbackLabel = generalOpt?.label ?? copy.decideYourself;
    appendTurn('assistant', copy.autoModeNotice);
    setSession((s) => ({
      ...s,
      selectedLocationIndex: fallbackIndex,
      selectedLocationLabel: fallbackLabel,
      chronological: false,
      orderMode: 'auto',
      step: 'awaiting_format',
    }));
    appendTurn('assistant', copy.askFormat);
  };

  const handleLocationSelect = (paragraphIndex: number, label: string) => {
    appendTurn('user', label);
    const ctx = session.analyzeResult?.ordering_context;

    if (shouldOfferOuterClauseMatch(ctx)) {
      appendTurn('assistant', ctx!.clarification_message);
      setSession((s) => ({
        ...s,
        selectedLocationIndex: paragraphIndex,
        selectedLocationLabel: label,
        chronological: false,
        step: 'awaiting_outer_clause_match',
      }));
      return;
    }

    const shouldAskChronological =
      !!session.analyzeResult?.has_clause_numbers ||
      !!ctx?.pasted_generic_numbering ||
      (session.analyzeResult?.client_requests.length ?? 0) > 1;

    setSession((s) => ({
      ...s,
      selectedLocationIndex: paragraphIndex,
      selectedLocationLabel: label,
      step: shouldAskChronological ? 'awaiting_chronological' : 'awaiting_format',
      orderMode: 'pasted',
    }));
    if (shouldAskChronological) {
      appendTurn('assistant', copy.askChronological);
    } else {
      appendTurn('assistant', copy.askFormat);
    }
  };

  const proceedToFormat = (orderMode: SkillOrderMode, notice?: string) => {
    if (notice) appendTurn('assistant', notice);
    setSession((s) => ({ ...s, orderMode, step: 'awaiting_format' }));
    appendTurn('assistant', copy.askFormat);
  };

  const handleChronologicalSelect = (yes: boolean) => {
    appendTurn('user', yes ? copy.chronologicalYes : copy.chronologicalNo);
    const ctx = session.analyzeResult?.ordering_context;

    if (!yes) {
      setSession((s) => ({ ...s, chronological: false, orderMode: 'pasted' }));
      if (shouldOfferOuterClauseMatch(ctx)) {
        appendTurn('assistant', ctx!.clarification_message);
        setSession((s) => ({ ...s, step: 'awaiting_outer_clause_match' }));
        return;
      }
      proceedToFormat('pasted');
      return;
    }

    setSession((s) => ({ ...s, chronological: true }));

    if (shouldOfferOuterClauseMatch(ctx)) {
      appendTurn('assistant', ctx!.clarification_message);
      setSession((s) => ({ ...s, step: 'awaiting_outer_clause_match' }));
      return;
    }

    proceedToFormat('generic_numbered');
  };

  const handleOuterClauseMatch = (yes: boolean) => {
    appendTurn('user', yes ? copy.askOuterClauseMatchYes : copy.askOuterClauseMatchNo);
    if (yes) {
      setSession((s) => ({ ...s, orderMode: 'outer_clause' }));
      proceedToFormat('outer_clause');
      return;
    }
    setSession((s) => ({ ...s, orderMode: 'append_at_location' }));
    proceedToFormat('append_at_location');
  };

  const handleFormatSelect = (mode: 'plain' | 'track_changes') => {
    appendTurn('user', mode === 'track_changes' ? copy.formatTrackChanges : copy.formatPlain);
    if (mode === 'track_changes') {
      setSession((s) => ({
        ...s,
        formatMode: mode,
        step: 'awaiting_author',
      }));
      appendTurn('assistant', copy.askAuthor);
    } else {
      if (!session.analyzeResult || session.selectedLocationIndex == null) return;
      setSession((s) => ({ ...s, formatMode: mode }));
      void runExecute({
        formatMode: mode,
        author: copy.defaultAuthor,
        analyzeResult: session.analyzeResult,
        selectedLocationIndex: session.selectedLocationIndex,
        selectedLocationLabel: session.selectedLocationLabel ?? '',
        chronological: session.chronological ?? false,
        orderMode: session.orderMode,
      });
    }
  };

  const runExecuteWithAuthor = (author: string) => {
    if (!session.analyzeResult || session.selectedLocationIndex == null) return;
    void runExecute({
      formatMode: 'track_changes',
      author,
      analyzeResult: session.analyzeResult,
      selectedLocationIndex: session.selectedLocationIndex,
      selectedLocationLabel: session.selectedLocationLabel ?? '',
      chronological: session.chronological ?? false,
      orderMode: session.orderMode,
    });
  };

  const handleAuthorMyName = () => {
    const author = profileAuthorName.trim() || copy.defaultAuthor;
    appendTurn('user', copy.authorMyName(author));
    runExecuteWithAuthor(author);
  };

  const handleAuthorUnknown = () => {
    appendTurn('user', copy.authorUnknown);
    runExecuteWithAuthor(copy.defaultAuthor);
  };

  const handleAuthorSomeoneElse = () => {
    appendTurn('user', copy.authorSomeoneElse);
    setSession((s) => ({ ...s, step: 'awaiting_author_custom' }));
    appendTurn('assistant', copy.askCustomAuthor);
  };

  const handleCustomAuthorSubmit = (authorText: string) => {
    const author = authorText.trim() || copy.defaultAuthor;
    appendTurn('user', author);
    runExecuteWithAuthor(author);
  };

  const runExecute = async (params: {
    formatMode: 'plain' | 'track_changes';
    author: string;
    analyzeResult: SkillAnalyzeResult;
    selectedLocationIndex: number;
    selectedLocationLabel: string;
    chronological: boolean;
    orderMode: SkillOrderMode;
  }) => {
    if (executingRef.current) return;
    executingRef.current = true;
    setBusy(true);
    setSession((s) => ({
      ...s,
      step: 'executing',
      error: null,
      formatMode: params.formatMode,
      author: params.author,
    }));
    appendTurn('assistant', copy.installing);

    try {
      const { analyzeResult, selectedLocationIndex, chronological, formatMode, author, orderMode, selectedLocationLabel } = params;

      const paras = await getDocumentParagraphsForSkill();

      const res = await apiService.executeAgentSkill({
        skillId: skill.id,
        clientRequests: analyzeResult.client_requests,
        paragraphs: paras,
        locationParagraphIndex: selectedLocationIndex,
        chronological,
        formatMode,
        author,
        language: SKILL_AGENT_LANGUAGE,
        orderMode,
        outerClauseRefs: analyzeResult.ordering_context?.outer_clause_refs,
        locationLabel: selectedLocationLabel,
        locationOptions: analyzeResult.location_options,
      });

      if (!res.success || !res.data) {
        throw new Error(res.message || 'Execution failed');
      }

      const executeResult: SkillExecuteResult = {
        insertions: (res.data.insertions || []).map((ins) => ({
          client_request_text: ins.client_request_text,
          target_paragraph_index: ins.target_paragraph_index,
          insert_mode: ins.insert_mode as 'after' | 'before' | 'replace',
          format: ins.format as 'plain' | 'track_changes',
          author: ins.author,
        })),
        summary: res.data.summary || '',
        insertion_strategy: res.data.insertion_strategy,
      };

      const { succeeded, failed, details } = await executeSkillInsertions(
        executeResult.insertions,
      );

      const summary =
        succeeded > 0
          ? copy.doneSummary(succeeded, failed)
          : copy.noneInserted;

      appendTurn('assistant', `${summary}\n\n${res.data.summary || ''}`);

      setSession((prev) => ({
        ...prev,
        step: 'done',
        executeResult,
        author,
        formatMode,
        error: failed > 0 ? details.join('\n') : null,
      }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Execution failed';
      appendTurn('assistant', copy.errorExecute(msg));
      setSession((prev) => ({ ...prev, step: 'done', error: msg }));
    } finally {
      executingRef.current = false;
      setBusy(false);
    }
  };

  const handleSubmit = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;

    switch (session.step) {
      case 'awaiting_paste':
        await handlePasteSubmit(trimmed);
        break;
      case 'awaiting_author_custom':
        handleCustomAuthorSubmit(trimmed);
        break;
      default:
        break;
    }
  };

  const showComposer =
    session.step === 'awaiting_paste' || session.step === 'awaiting_author_custom';

  const composerPlaceholder =
    session.step === 'awaiting_author_custom'
      ? copy.placeholderAuthor
      : copy.placeholderPaste;

  const composerFollowUp = session.turns.some((turn) => turn.role === 'user');

  const myNameLabel = copy.authorMyName(profileAuthorName.trim() || copy.defaultAuthor);

  const locationOptions = session.analyzeResult?.location_options ?? [];

  return (
    <div className="skill-runner">
      <div className="skill-runner-header">
        <button type="button" className="skill-runner-back" onClick={onBack} disabled={busy}>
          <ArrowLeft size={14} aria-hidden />
          Workflows
        </button>
        <span className="skill-runner-title">{displaySkill.name}</span>
      </div>

      <div className="skill-runner-thread">
        {session.turns.map((turn) => (
          <div
            key={turn.id}
            className={`skill-runner-turn skill-runner-turn--${turn.role}`}
            dir="auto"
          >
            {turn.role === 'assistant' && <Bot size={14} className="skill-runner-turn-icon" aria-hidden />}
            <div className="skill-runner-turn-content">{turn.content}</div>
          </div>
        ))}

        {session.step === 'awaiting_location' && locationOptions.length > 0 && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={handleAutoLocationSelect}
            >
              {copy.decideYourself}
            </button>
            {locationOptions.map((opt) => (
              <button
                key={`${opt.paragraph_index}-${opt.label}`}
                type="button"
                className="skill-runner-option-btn"
                disabled={busy}
                onClick={() => handleLocationSelect(opt.paragraph_index, opt.label)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {session.step === 'awaiting_chronological' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleChronologicalSelect(true)}
            >
              {copy.chronologicalYes}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleChronologicalSelect(false)}
            >
              {copy.chronologicalNo}
            </button>
          </div>
        )}

        {session.step === 'awaiting_outer_clause_match' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleOuterClauseMatch(true)}
            >
              {copy.askOuterClauseMatchYes}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleOuterClauseMatch(false)}
            >
              {copy.askOuterClauseMatchNo}
            </button>
          </div>
        )}

        {session.step === 'awaiting_format' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleFormatSelect('track_changes')}
            >
              {copy.formatTrackChanges}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleFormatSelect('plain')}
            >
              {copy.formatPlain}
            </button>
          </div>
        )}

        {session.step === 'awaiting_author' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={handleAuthorMyName}
            >
              {myNameLabel}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={handleAuthorUnknown}
            >
              {copy.authorUnknown}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={handleAuthorSomeoneElse}
            >
              {copy.authorSomeoneElse}
            </button>
          </div>
        )}

        {busy && (
          <div className="skill-runner-busy">
            <RefreshCw size={12} className="spinning" aria-hidden />
            <span>Working…</span>
          </div>
        )}

        {session.error && session.step === 'done' && (
          <div className="skill-runner-error">{session.error}</div>
        )}

        <div ref={scrollRef} />
      </div>

      {showComposer && (
        <div className="skill-runner-composer">
          <ChatComposer
            value={input}
            onChange={setInput}
            onSubmit={async ({ text }) => {
              await handleSubmit(text);
              setInput('');
            }}
            disabled={busy}
            submitting={busy}
            placeholder={composerPlaceholder}
            layout="sticky"
            followUp={composerFollowUp}
            submitTitle="Send"
            showAgentModeSelector={false}
          />
        </div>
      )}
    </div>
  );
};
